import { useEffect, useMemo, useState, useSyncExternalStore, type ReactNode } from 'react';
import {
  applyTheme,
  DARK_QUERY,
  effectiveTheme,
  readPreference,
  systemPrefersDark,
  ThemeContext,
  writePreference,
  type ThemePreference,
  type ThemeState,
} from './theme';

function subscribeToSystemTheme(onChange: () => void): () => void {
  if (typeof window.matchMedia !== 'function') return () => {};
  const query = window.matchMedia(DARK_QUERY);
  query.addEventListener('change', onChange);
  return () => query.removeEventListener('change', onChange);
}

/**
 * Holds the theme preference of `user` (the `/me` id, else the name), applies it to <html>, and follows
 * `prefers-color-scheme` live while the preference is System (spec 6.1.9). Give it `key={user}` so a
 * different user starts from their own stored preference.
 */
export function ThemeProvider({ user, children }: { user: string | null; children: ReactNode }) {
  const [preference, setPreferenceState] = useState<ThemePreference>(() => readPreference(user));
  const prefersDark = useSyncExternalStore(subscribeToSystemTheme, systemPrefersDark, () => false);

  const effective = effectiveTheme(preference, prefersDark);
  useEffect(() => applyTheme(effective), [effective]);

  const state = useMemo<ThemeState>(
    () => ({
      preference,
      effective,
      setPreference: (next) => {
        writePreference(user, next);
        setPreferenceState(next);
      },
    }),
    [preference, effective, user],
  );
  return <ThemeContext.Provider value={state}>{children}</ThemeContext.Provider>;
}
